// src/admin/AboutAdmin.jsx
import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from './AdminLayout';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import TextAlign from '@tiptap/extension-text-align';
import { Extension } from '@tiptap/core';
import {
  getAboutContent, updateAboutBio,
  uploadTriptychPhoto, deleteTriptychPhoto,
  togglePhotoAboutStyle, getAdminPhotos,
} from '../config/api';

const STYLE_MAX = 6;

const DEFAULT_BIO_HTML = `<p>I'm a simple girl looking for magic in everyday moments — celebrating everything and everyone, imperfect and perfect.</p><p>I believe in capturing the unposed, authentic moments that tell your unique story. From the anticipation of maternity to the tiny details of newborn life, my lens seeks the real, raw beauty in every frame.</p><p>Based in Pune, I work with families who value natural, emotion-driven photography over perfect poses. Every session is a collaboration, and every photo is a piece of your story.</p>`;

// Custom FontSize extension (font-size not available as free TipTap package)
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() { return { types: ['textStyle'] }; },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: element => element.style.fontSize || null,
          renderHTML: attributes => {
            if (!attributes.fontSize) return {};
            return { style: `font-size: ${attributes.fontSize}` };
          },
        },
      },
    }];
  },
  addCommands() {
    return {
      setFontSize: fontSize => ({ chain }) =>
        chain().setMark('textStyle', { fontSize }).run(),
      unsetFontSize: () => ({ chain }) =>
        chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

function EditorToolbar({ editor }) {
  if (!editor) return null;
  const btn = (active, action, label) => (
    <button
      key={label}
      onMouseDown={e => { e.preventDefault(); action(); }}
      style={{
        padding: '4px 8px', fontSize: 12, cursor: 'pointer',
        background: active ? 'var(--color-accent)' : 'transparent',
        color: active ? '#0F0F0F' : 'var(--color-muted)',
        border: '1px solid var(--color-border)', borderRadius: 2, marginRight: 4,
      }}
    >{label}</button>
  );
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8, padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
      {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), 'B')}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), 'I')}
      {btn(editor.isActive('underline'), () => editor.chain().focus().toggleUnderline().run(), 'U')}
      <select onChange={e => editor.chain().focus().setFontFamily(e.target.value).run()} defaultValue=""
        style={{ fontSize: 11, background: 'var(--color-surface)', color: 'var(--color-muted)', border: '1px solid var(--color-border)', borderRadius: 2, padding: '2px 6px' }}>
        <option value="">Font</option>
        <option value="DM Sans">DM Sans</option>
        <option value="Playfair Display">Playfair Display</option>
        <option value="Georgia">Georgia</option>
        <option value="Arial">Arial</option>
      </select>
      <select onChange={e => editor.chain().focus().setFontSize(e.target.value).run()} defaultValue=""
        style={{ fontSize: 11, background: 'var(--color-surface)', color: 'var(--color-muted)', border: '1px solid var(--color-border)', borderRadius: 2, padding: '2px 6px' }}>
        <option value="">Size</option>
        {[10, 12, 13, 14, 16, 18, 21, 24, 32, 48].map(s => (
          <option key={s} value={`${s}px`}>{s}px</option>
        ))}
      </select>
      <input type="color" title="Text colour"
        onChange={e => editor.chain().focus().setColor(e.target.value).run()}
        style={{ width: 28, height: 28, padding: 2, border: '1px solid var(--color-border)', borderRadius: 2, background: 'transparent', cursor: 'pointer' }} />
      {btn(editor.isActive({ textAlign: 'left' }), () => editor.chain().focus().setTextAlign('left').run(), '\u2B05')}
      {btn(editor.isActive({ textAlign: 'center' }), () => editor.chain().focus().setTextAlign('center').run(), '\u2194')}
      {btn(editor.isActive({ textAlign: 'right' }), () => editor.chain().focus().setTextAlign('right').run(), '\u27A1')}
    </div>
  );
}

export default function AboutAdmin() {
  const [bioStatus, setBioStatus] = useState('');
  const [triptych, setTriptych] = useState([
    { position: 1, url: null },
    { position: 2, url: null },
    { position: 3, url: null },
  ]);
  const [triptychUploading, setTriptychUploading] = useState({});
  const [triptychError, setTriptychError] = useState({});
  const fileInputRefs = useRef({});
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [styleIds, setStyleIds] = useState(new Set());
  const [bioContent, setBioContent] = useState(null); // null = not yet loaded

  const editor = useEditor({
    extensions: [
      StarterKit, TextStyle, Color, FontFamily, FontSize,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: '',
  });

  // Load all data once on mount (independent of editor readiness)
  useEffect(() => {
    Promise.all([getAboutContent(), getAdminPhotos(null, 0, 100)])
      .then(([aboutRes, photosRes]) => {
        setBioContent(aboutRes.data?.bio_html || DEFAULT_BIO_HTML);
        if (aboutRes.data?.triptych) {
          setTriptych([1, 2, 3].map(pos => {
            const found = aboutRes.data.triptych.find(t => t.position === pos);
            return { position: pos, url: found?.url || null };
          }));
        }
        if (aboutRes.data?.style_photos) {
          setStyleIds(new Set(aboutRes.data.style_photos.map(p => p.id)));
        }
        if (photosRes.data?.data) setGalleryPhotos(photosRes.data.data);
      })
      .catch(() => { setBioContent(DEFAULT_BIO_HTML); });
  }, []);

  // Set editor content once both the editor instance and loaded content are ready
  useEffect(() => {
    if (!editor || bioContent === null) return;
    editor.commands.setContent(bioContent);
  }, [editor, bioContent]);

  const saveBio = async () => {
    setBioStatus('saving');
    try {
      await updateAboutBio(editor.getHTML());
      setBioStatus('saved');
      setTimeout(() => setBioStatus(''), 2000);
    } catch {
      setBioStatus('error');
    }
  };

  const handleTriptychUpload = async (position, file) => {
    if (!file) return;
    setTriptychUploading(prev => ({ ...prev, [position]: true }));
    setTriptychError(prev => ({ ...prev, [position]: null }));
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await uploadTriptychPhoto(position, formData);
      setTriptych(prev => prev.map(t => t.position === position ? { ...t, url: res.data.url } : t));
      if (fileInputRefs.current[position]) fileInputRefs.current[position].value = '';
    } catch {
      setTriptychError(prev => ({ ...prev, [position]: 'Upload failed' }));
    } finally {
      setTriptychUploading(prev => ({ ...prev, [position]: false }));
    }
  };

  const handleTriptychDelete = async (position) => {
    setTriptychError(prev => ({ ...prev, [position]: null }));
    try {
      await deleteTriptychPhoto(position);
      setTriptych(prev => prev.map(t => t.position === position ? { ...t, url: null } : t));
    } catch {
      setTriptychError(prev => ({ ...prev, [position]: 'Delete failed' }));
    }
  };

  const handleStyleToggle = async (photoId) => {
    const isSelected = styleIds.has(photoId);
    if (!isSelected && styleIds.size >= STYLE_MAX) return;
    setStyleIds(prev => {
      const next = new Set(prev);
      isSelected ? next.delete(photoId) : next.add(photoId);
      return next;
    });
    try {
      await togglePhotoAboutStyle(photoId);
    } catch {
      setStyleIds(prev => {
        const next = new Set(prev);
        isSelected ? next.add(photoId) : next.delete(photoId);
        return next;
      });
    }
  };

  return (
    <AdminLayout title="About Page">
    <div style={{ maxWidth: 960 }}>

      {/* ── Bio ── */}
      <section style={{ marginBottom: 56 }}>
        <h2 style={{ fontFamily: 'var(--font-body)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--color-accent)', marginBottom: 16 }}>
          Bio Text
        </h2>
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 4, padding: 16, background: 'var(--color-surface)' }}>
          <EditorToolbar editor={editor} />
          <EditorContent editor={editor} style={{ minHeight: 200, color: 'var(--color-text)', fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.8 }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12 }}>
          <button onClick={saveBio} disabled={bioStatus === 'saving'}
            style={{ padding: '10px 24px', background: 'var(--color-accent)', color: '#0F0F0F', border: 'none', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: bioStatus === 'saving' ? 'not-allowed' : 'pointer', opacity: bioStatus === 'saving' ? 0.7 : 1 }}>
            {bioStatus === 'saving' ? 'Saving\u2026' : 'Save Bio'}
          </button>
          {bioStatus === 'saved' && <span style={{ fontSize: 12, color: 'var(--color-success)' }}>Saved</span>}
          {bioStatus === 'error' && <span style={{ fontSize: 12, color: 'var(--color-error)' }}>Save failed</span>}
        </div>
      </section>

      {/* ── Triptych ── */}
      <section style={{ marginBottom: 56 }}>
        <h2 style={{ fontFamily: 'var(--font-body)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--color-accent)', marginBottom: 8 }}>
          Triptych Photos
        </h2>
        <p style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 16 }}>
          3 portrait photos — same shoot, 3 angles (turned right · front facing · turned left)
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          {triptych.map(({ position, url }) => (
            <div key={position} style={{ border: '1px solid var(--color-border)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ aspectRatio: '3/4', background: 'var(--color-surface)', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {url
                  ? <img src={url} alt={`Triptych ${position}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  : <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Position {position}</span>
                }
                {triptychUploading[position] && (
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: '#fff', fontSize: 12 }}>Uploading\u2026</span>
                  </div>
                )}
              </div>
              <div style={{ padding: '8px 12px', display: 'flex', gap: 8 }}>
                <label style={{ flex: 1, padding: '6px 0', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--color-accent)', cursor: 'pointer', textAlign: 'center', border: '1px solid rgba(212,168,67,0.4)', borderRadius: 2 }}>
                  {url ? 'Replace' : 'Upload'}
                  <input type="file" accept="image/*" style={{ display: 'none' }}
                    ref={el => { fileInputRefs.current[position] = el; }}
                    onChange={e => handleTriptychUpload(position, e.target.files[0])} />
                </label>
                {url && (
                  <button onClick={() => handleTriptychDelete(position)}
                    style={{ padding: '6px 10px', fontSize: 11, background: 'transparent', border: '1px solid var(--color-border)', color: 'var(--color-muted)', cursor: 'pointer', borderRadius: 2 }}>
                    &times;
                  </button>
                )}
              </div>
              {triptychError[position] && (
                <div style={{ padding: '4px 12px 8px', fontSize: 11, color: 'var(--color-error)' }}>
                  {triptychError[position]}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── Style Lightbox Photos ── */}
      <section>
        <h2 style={{ fontFamily: 'var(--font-body)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--color-accent)', marginBottom: 8 }}>
          Style Lightbox Photos
        </h2>
        <p style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 4 }}>
          Select up to 6 photos from your gallery for the "About My Style" lightbox.
        </p>
        <p style={{ fontSize: 12, color: styleIds.size >= STYLE_MAX ? 'var(--color-accent)' : 'var(--color-muted)', marginBottom: 16 }}>
          {styleIds.size} / {STYLE_MAX} selected
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
          {galleryPhotos.map(photo => {
            const selected = styleIds.has(photo.id);
            const disabled = !selected && styleIds.size >= STYLE_MAX;
            return (
              <div key={photo.id} data-testid={`style-toggle-${photo.id}`}
                onClick={() => !disabled && handleStyleToggle(photo.id)}
                style={{ aspectRatio: '1', overflow: 'hidden', position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer', border: selected ? '2px solid var(--color-accent)' : '2px solid transparent', borderRadius: 2, opacity: disabled ? 0.4 : 1 }}>
                <img src={photo.thumbnail_url || photo.url} alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                {selected && (
                  <div style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%', background: 'var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 11, color: '#0F0F0F', fontWeight: 700 }}>\u2713</span>
                  </div>
                )}
              </div>
            );
          })}
          {galleryPhotos.length === 0 && (
            <p style={{ color: 'var(--color-muted)', fontSize: 13, gridColumn: '1/-1' }}>No photos in gallery yet.</p>
          )}
        </div>
      </section>
    </div>
    </AdminLayout>
  );
}
